import React, { FC, ReactElement, useEffect, useState, useRef } from "react";
import ChatInput from "../components/chatInput";
import config from "../config";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import ChatItemDetails from "../components/chatItemDetails";
import { mergeStyles, Spinner, SpinnerSize } from "@fluentui/react";
import Markdown from "markdown-to-jsx";
import { useMediaQuery } from "react-responsive";
import { getThemeColors, getComponentColors, Theme } from "../theme/colors";
import { getLogoImages, isShellFn } from "../layout/chatSidebar";
import FollowUpSuggestions from "../components/followUpSuggestions";

export interface ChatInputData {
  message: string;
  question_id?: string;
  response: {
    answer: string;
    sql: {
      possible_to_answer: boolean;
      info: string;
      sql_cmd?: string;
    };
    history: string;
    sql_id: number;
    definitions: string[];
  };
  feedback: {
    display_name: string;
    notes: string;
    thumbs: boolean | null;
    feedback: string;
  };
  agent_messages?: string;
}

export interface ErrorResponseData {
  error?: string;
  details?: string;
  original_q?: string | null;
}

export const commonIconImageStyle = mergeStyles({
  height: 40,
  width: 40,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginRight: 10,
});

const ChatPage: FC<{
  username: string;
  convoStarters: string[];
  theme: Theme;
  isCollapsed: boolean;
  devNumber?: number;
  autoCompleteDictionary?: Record<string, string>;
}> = (props): ReactElement => {
  const {
    username,
    convoStarters,
    theme,
    isCollapsed,
    devNumber,
    autoCompleteDictionary,
  } = props;
  const { chatId, shared } = useParams();
  const location = useLocation();

  const [chatHistory, setChatHistory] = useState<ChatInputData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ipAddress, setIpAddress] = useState<string>("");
  const [maxWidth, setMaxWidth] = useState("800px");
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const [selectedConvoStarter, setSelectedConvoStarter] = useState<
    string | null
  >(null);
  const [sharedChat, setSharedChat] = useState<boolean>(
    (shared?.length ?? 0) > 0
  );

  // "Fake" progress from 0 → ~63% while loading, then bump 1% if stuck for 3 s
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [hasFinished, setHasFinished] = useState<boolean>(false);

  // Refs to track the "real" value and last-change timestamp
  const loadingProgressRef = useRef<number>(0);
  const lastProgressChangeRef = useRef<number>(Date.now());

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isTablet = useMediaQuery({ maxWidth: 810 });
  const isMobile = useMediaQuery({ maxWidth: 414 });

  const colors = getThemeColors(username, theme);
  const chatPageColors = getComponentColors("chatPage", theme);

  const extractHistory = (history: ChatInputData[]) => {
    if (history.length === 0) return "";
    return history[history.length - 1].response.history;
  };

  const extractAgentMessages = (history: ChatInputData[]) => {
    if (history.length === 0) return "";
    return history[history.length - 1].agent_messages;
  };

  useEffect(() => {
    function updateMaxWidth() {
      const screenWidth = window.innerWidth;
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );

      let availableWidth;

      if (isMobile) {
        const menuWidthInRem = 3;
        const menuWidth = menuWidthInRem * rootFontSize;
        availableWidth = screenWidth - menuWidth;
      } else {
        const menuWidthInRem = isCollapsed ? 2 : 16;
        const menuWidth = menuWidthInRem * rootFontSize;
        availableWidth = screenWidth - menuWidth;

        const widthMultiplier = isCollapsed ? 0.95 : 0.85;
        availableWidth *= widthMultiplier;
      }

      setMaxWidth(`${availableWidth}px`);
    }

    updateMaxWidth();
    window.addEventListener("resize", updateMaxWidth);
    return () => window.removeEventListener("resize", updateMaxWidth);
  }, [isCollapsed, isMobile]);

  useEffect(() => {
    const getChatHistoryByID = async (id: string) => {
      setIsLoading(true);
      if (id === "0") {
        setIsLoading(false);
        setChatHistory([]);
        setSharedChat(false);
        return;
      }

      try {
        const response = await axios.post(
          `${config.api.baseUrl}/chat/history`,
          { chat_id: id, username, shared: (shared?.length ?? 0) > 0 }
        );
        setChatHistory(response.data.chat);
        setSharedChat(response.data.shared);
      } catch (error: any) {
        console.error("Error fetching chat history:", error);
        alert(error.response.data.error);
      } finally {
        setIsLoading(false);
      }
    };
    if (chatId) {
      getChatHistoryByID(chatId);
    }
  }, [chatId, location.key, shared?.length, sharedChat, username]);

  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await axios.post(`${config.api.baseUrl}/sql/colab`, {
          username,
        });
        if (response.data && response.data.rows.length > 0) {
          setIpAddress(response.data.rows[0].ml_aurora);
        }
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIpAddress();
  }, [username]);

  const submittedFromChatInput = async (
    message: string,
    isConvoStarter?: boolean
  ) => {
    if (!ipAddress) {
      console.error("No IP address found. Please try again later.");
      return;
    }

    // Set selected conversation starter if this came from a starter button
    if (isConvoStarter) {
      setSelectedConvoStarter(message);
    }

    try {
      // START LOADING
      setHasFinished(false);
      setIsLoading(true);
      setLoadingProgress(0);
      loadingProgressRef.current = 0;
      lastProgressChangeRef.current = Date.now();

      const axiosResponse = await axios.post(`${config.api.baseUrl}/chat`, {
        message,
        gradio_url: ipAddress.includes("74.235.23.104")
          ? `${ipAddress}/user_question_strands`
          : `${ipAddress}/user_question`,
        history: extractHistory(chatHistory),
        username,
        agent_messages: extractAgentMessages(chatHistory),
      });

      const {
        answer,
        sql,
        history,
        definitions,
        question_id,
        follow_up_questions,
        agent_messages,
      } = axiosResponse.data;
      const savedChat = { answer, sql, history, sql_id: 0, definitions };

      setFollowUpSuggestions(follow_up_questions);

      setChatHistory((prevHistory) => [
        ...prevHistory,
        {
          message,
          question_id,
          response: savedChat,
          feedback: {
            display_name: "",
            notes: "",
            thumbs: null,
            feedback: "",
          },
          agent_messages,
        },
      ]);
    } catch (error) {
      console.error("Error while calling the chat route:", error);

      let errorMessage = "An error occurred. Please try again.";
      if (axios.isAxiosError(error) && error.response) {
        const data = error.response.data as ErrorResponseData;
        if (data && data.error) {
          errorMessage = data.error;
          if (data.details) {
            errorMessage += ` -- ${data.details}`;
          }
        } else if (error.response.statusText) {
          errorMessage = error.response.statusText;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      const errorResponse = {
        answer: errorMessage,
        sql: {
          possible_to_answer: false,
          info: "",
        },
        history: "",
        sql_id: 0,
        definitions: [],
      };

      setChatHistory((prevHistory) => [
        ...prevHistory,
        {
          message,
          response: errorResponse,
          feedback: {
            display_name: "",
            notes: "",
            thumbs: null,
            feedback: "",
          },
        },
      ]);
    } finally {
      // END LOADING → snap to 100%, then hide after 300 ms
      setHasFinished(true);
      setTimeout(() => {
        setIsLoading(false);
        setLoadingProgress(0);
        loadingProgressRef.current = 0;
        setHasFinished(false);
        setSelectedConvoStarter(null);
      }, 300);
    }
  };

  const getHightForIcon = (username: string): string => {
    if (isShellFn(username)) {
      return "91%";
    }
    return "100%";
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const slowDownpct = 67;

    if (isLoading) {
      // Initialize refs at start of loading
      loadingProgressRef.current = 0;
      lastProgressChangeRef.current = Date.now();
      setLoadingProgress(0);

      intervalId = setInterval(() => {
        const prev = loadingProgressRef.current;
        const now = Date.now();
        let next = prev;

        if (prev < slowDownpct) {
          // Much slower asymptotic approach - take only 2% of remaining distance every 200ms
          next = prev + (slowDownpct - prev) * 0.02;
          next = parseFloat(next.toFixed(2));

          // Update progress and timestamp
          loadingProgressRef.current = next;
          lastProgressChangeRef.current = now;
          setLoadingProgress(next);
        } else {
          // If we've reached or exceeded slowDownpct and been stuck for 2+ seconds, bump by 1%
          if (now - lastProgressChangeRef.current >= 2000) {
            next = Math.min(prev + 1, 95); // Cap at 95% to leave room for the final jump to 100%
            loadingProgressRef.current = next;
            lastProgressChangeRef.current = now;
            setLoadingProgress(next);
          }
        }
      }, 200); // Slower interval: every 200ms instead of 100ms
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isLoading]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 30px)",
        padding: isTablet ? "10px" : "5px",
        marginTop: "25px",
        boxSizing: "border-box",
        maxWidth: maxWidth,
        marginLeft: "auto",
        marginRight: "auto",
        backgroundColor: chatPageColors.background || colors.background,
        color: colors.text,
        minWidth: 0,
        overflowX: "hidden",
        scrollbarWidth: "none",
      }}
    >
      {chatHistory.length === 0 && chatId === "0" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            height: "70vh",
            paddingTop: "20px",
          }}
        >
          {!isTablet && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "50%",
                justifyContent: "flex-end",
              }}
            >
              <img
                src={getLogoImages(username, theme).collapsed}
                alt="Logo"
                style={{ marginBottom: "20px", width: "300px" }}
              />
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isTablet
                ? "repeat(2, 1fr)"
                : "repeat(3, 1fr)",
              gap: "10px",
              marginTop: "10px",
              width: "100%",
            }}
          >
            {convoStarters.length > 0 &&
              convoStarters.map((starter, index) => {
                const isSelected = selectedConvoStarter === starter;

                return (
                  <button
                    key={index}
                    style={{
                      padding: "25px 20px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      border: `1px solid ${colors.border}`,
                      borderRadius: "20px",
                      backgroundColor: isLoading
                        ? isSelected
                          ? colors.convoStarterHover
                          : "#999"
                        : isSelected
                        ? colors.convoStarterHover
                        : colors.convoStarterBg,
                      color: colors.text,
                      lineHeight: "1.5",
                      textAlign: "center",
                      wordWrap: "break-word",
                      opacity: isLoading ? (isSelected ? 0.8 : 0.6) : 1,
                    }}
                    disabled={isLoading}
                    onClick={() => submittedFromChatInput(starter, true)}
                    onMouseEnter={(e) => {
                      if (!isLoading && !isSelected) {
                        e.currentTarget.style.backgroundColor =
                          colors.convoStarterHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading && !isSelected) {
                        e.currentTarget.style.backgroundColor =
                          colors.convoStarterBg;
                      }
                    }}
                  >
                    {starter}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      <div
        style={{
          flexGrow: 1,
          overflowY: "auto",
          paddingBottom: "10px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          scrollBehavior: "smooth",
          minWidth: 0,
        }}
        ref={messagesEndRef}
      >
        {chatHistory.map((chatItem, index) => (
          <div
            key={index}
            style={{ display: "flex", flexDirection: "column", gap: "5px" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingTop: "5px",
                paddingBottom: "5px",
                border: `1px solid ${colors.border}`,
                gap: "10px",
                borderRadius: "20px",
                backgroundColor: colors.optionsBg,
                padding: "5px 15px",
                width: "fit-content",
                justifyContent: "flex-end",
                alignSelf: "flex-end",
              }}
            >
              {chatItem.message}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingTop: "5px",
                paddingBottom: "5px",
                gap: "10px",
              }}
            >
              <div className={commonIconImageStyle}>
                <img
                  src={getLogoImages(username, theme).collapsed}
                  alt="Logo"
                  style={{ height: getHightForIcon(username), width: "auto" }}
                />
              </div>{" "}
              <div>
                <Markdown>{chatItem.response.answer}</Markdown>
              </div>
            </div>
            <ChatItemDetails
              username={username}
              chatId={chatId}
              theme={theme}
              chatItem={chatItem}
            />
            {(devNumber ?? 0) > 0 && (devNumber ?? 0) < 99 && (
              <FollowUpSuggestions
                suggestions={followUpSuggestions}
                theme={theme}
                username={username}
                onSuggestionClick={submittedFromChatInput}
                isDisabled={isLoading}
              />
            )}
          </div>
        ))}
      </div>

      {!sharedChat && (
        <ChatInput
          theme={theme}
          onMessageSubmit={submittedFromChatInput}
          isDisabled={isLoading}
          username={username}
          autoComplete={autoCompleteDictionary}
        />
      )}

      {sharedChat && (
        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
          <div style={{ fontSize: "12px", color: "gray" }}>
            This chat is read only
          </div>
        </div>
      )}

      {/* -------- CUSTOM PROGRESS BAR AT BOTTOM -------- */}
      <div
        style={{
          textAlign: "center",
          marginTop: "10px",
          height: "25px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: colors.text,
              gap: "10px",
            }}
          >
            {/* Show percentage number */}
            <div
              style={{
                fontSize: "14px",
                fontWeight: "500",
                minWidth: "35px",
              }}
            >
              {hasFinished
                ? // When finished, show 100% briefly
                  "100%"
                : `${Math.round(loadingProgress)}%`}
            </div>

            {/* Bar track */}
            <div
              style={{
                width: "100px",
                height: "4px",
                backgroundColor: "rgba(59, 145, 97, 0.3)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${hasFinished ? 100 : Math.max(loadingProgress, 0)}%`,
                  height: "100%",
                  backgroundColor: colors.text,
                  transition: hasFinished
                    ? "width 0.2s ease-in"
                    : "width 0.1s ease-out",
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChatPage;
